import { useCallback, useEffect, useState } from "react";
import { ALL_PLATFORM_MODULES, SECURITY_MODULES, localizeModule, type SecurityModuleMeta } from "../data/securityModules";
import { pick, type Lang, type Loc } from "../utils/customModule";
import { authFetch } from "../utils/authFetch";

type CustomModuleTitle = { id: string; topicId: string; title: Loc; shortTitle: Loc };

// Abschlüsse auf den am 2026-08-13 entfallenen Modulen bleiben in der Datenbank
// stehen; ihre IDs kennt der Katalog nicht mehr.
const LEGACY_MODULE_TITLES: Record<string, string> = {
  "phishing-basics": "Phishing, E-Mail & Surfen",
  "password-security": "Passwort & MFA",
  "social-engineering": "Social Engineering",
  "usb-physical-security": "Physical Security",
  "remote-work-security": "Homeoffice, Mobile & Cloud",
  "occupational-safety-health": "Arbeitssicherheit",
  "ai-security-awareness": "KI-Security",
  "mfa-account-security": "MFA & Kontosicherheit",
  "email-security": "E-Mail-Sicherheit",
  "secure-browsing": "Sicheres Surfen",
  "malware-ransomware": "Malware & Ransomware",
  "mobile-device-security": "Mobile Geräte",
  "data-protection-dsgvo": "Datenschutz & DSGVO",
  "incident-response": "Vorfallreaktion",
  "cloud-security": "Cloud-Sicherheit",
  "insider-threats": "Insider-Bedrohungen",
  "phishing": "Phishing Grundlagen",
  "password": "Passwortsicherheit",
  "social": "Social Engineering",
};

function findIn(list: SecurityModuleMeta[], moduleId: string, topicId: string): SecurityModuleMeta | undefined {
  return (moduleId ? list.find(m => m.id === moduleId) : undefined)
    ?? (topicId ? list.find(m => m.topicId === topicId) : undefined)
    ?? (topicId ? list.find(m => m.id === topicId) : undefined);
}

/**
 * Titel eines Moduls zu einem Abschluss-Datensatz – für lizenzierte, nicht mehr
 * lizenzierte, eigene und entfallene Module. Die ID erscheint nur, wenn es das
 * Modul nirgends mehr gibt.
 */
export function useModuleTitle(tenantId: string | null | undefined, lang: Lang = "de"): (moduleId?: string | null, topicId?: string | null) => string {
  const [customModules, setCustomModules] = useState<CustomModuleTitle[]>([]);

  useEffect(() => {
    if (!tenantId) return;
    authFetch(`/api/phishing-campaigns?tenantId=${encodeURIComponent(tenantId)}&customModules=1`)
      .then(r => r.ok ? r.json() : { customModules: [] })
      .then((data: { customModules?: CustomModuleTitle[] }) => setCustomModules(data.customModules ?? []))
      .catch(() => setCustomModules([]));
  }, [tenantId]);

  return useCallback((moduleId, topicId) => {
    const mid = moduleId ?? "";
    const tid = topicId ?? "";
    // SECURITY_MODULES zuerst: dort stehen die im GA-Portal angepassten Titel.
    const platform = findIn(SECURITY_MODULES, mid, tid) ?? findIn(ALL_PLATFORM_MODULES, mid, tid);
    if (platform) return localizeModule(platform, lang).shortTitle || platform.title;
    const custom = customModules.find(m => m.id === mid || m.id === tid || m.topicId === tid);
    if (custom) return pick(custom.shortTitle, lang) || pick(custom.title, lang) || custom.id;
    return LEGACY_MODULE_TITLES[mid] ?? LEGACY_MODULE_TITLES[tid] ?? (mid || tid);
  }, [customModules, lang]);
}
